import React, { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import {
    NativeScrollEvent,
    NativeSyntheticEvent,
    Pressable,
    ScrollView,
    StyleSheet,
    TextInput,
    ViewStyle,
} from 'react-native';

import { CloseSmallIcon } from '@/assets/icons/CloseSmallIcon';
import { DesktopSearchIcon } from '@/assets/icons/desktopSearch';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { textVariants } from '@/components/lib/typography';
import { useHover } from '@/feature/responsive-design/hooks/use-hover';
import { withOpacity } from '@/styles/styles';

import { DesktopModal } from './DesktopModal';

const PANEL_WIDTH = 440;
const PANEL_HEIGHT = 548;
const END_REACHED_OFFSET = 300;

const DesktopSearchScrolledContext = createContext(false);

type DesktopSearchModalProps = {
    visible: boolean;
    onClose: () => void;
    query: string;
    onChangeQuery: (query: string) => void;
    placeholder: string;
    closeAccessibilityLabel?: string;
    resetScrollKey?: string;
    onEndReached?: () => void;
    children?: ReactNode;
};

type SearchSectionProps = {
    label: string;
    children: ReactNode;
};

export const DesktopSearchSection = ({ label, children }: SearchSectionProps) => {
    const scrolled = useContext(DesktopSearchScrolledContext);

    return (
        <Box>
            <Box
                px="s16"
                pb="s12"
                backgroundColor={ds.colorToken.background.neutral.subtler}
                borderBottomWidth={1}
                borderColor={scrolled ? ds.colorToken.border.strong : ds.colorToken.background.neutral.subtler}
                style={styles.sticky}
            >
                <Text variant="labelMedium" color={ds.colorToken.text.secondary} textTransform="uppercase">
                    {label}
                </Text>
            </Box>
            <Box gap="s2">{children}</Box>
        </Box>
    );
};

type SearchScrollAreaProps = {
    resetKey: string;
    onEndReached?: () => void;
    children?: ReactNode;
};

const SearchScrollArea = ({ resetKey, onEndReached, children }: SearchScrollAreaProps) => {
    const scrollRef = useRef<ScrollView>(null);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        scrollRef.current?.scrollTo({ y: 0, animated: false });
    }, [resetKey]);

    const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
        setScrolled(contentOffset.y > 0);

        if (layoutMeasurement.height + contentOffset.y >= contentSize.height - END_REACHED_OFFSET) {
            onEndReached?.();
        }
    };

    return (
        <DesktopSearchScrolledContext.Provider value={scrolled}>
            <ScrollView
                ref={scrollRef}
                style={styles.scroll}
                contentContainerStyle={styles.scrollContent}
                onScroll={handleScroll}
                scrollEventThrottle={16}
                keyboardShouldPersistTaps="handled"
            >
                {children}
            </ScrollView>
        </DesktopSearchScrolledContext.Provider>
    );
};

export const DesktopSearchModal = ({
    visible,
    onClose,
    query,
    onChangeQuery,
    placeholder,
    closeAccessibilityLabel,
    resetScrollKey,
    onEndReached,
    children,
}: DesktopSearchModalProps) => {
    const { hovered: closeHovered, hoverHandlers: closeHoverHandlers } = useHover();

    return (
        <DesktopModal
            visible={visible}
            onClose={onClose}
            width={PANEL_WIDTH}
            height={PANEL_HEIGHT}
            gap="s8"
            pt="s8"
            borderRadius="r20"
        >
            <Row alignItems="center" gap="s8" pl="s16" pr="s12" py="s4">
                <DesktopSearchIcon />
                <TextInput
                    autoFocus
                    value={query}
                    onChangeText={onChangeQuery}
                    placeholder={placeholder}
                    placeholderTextColor={ds.colorValue.text.secondary}
                    style={styles.input}
                    testID="desktop-search-input"
                />
                <Pressable
                    {...closeHoverHandlers}
                    onPress={onClose}
                    style={styles.closeButton}
                    accessibilityRole="button"
                    accessibilityLabel={closeAccessibilityLabel}
                    testID="desktop-search-close"
                >
                    <CloseSmallIcon color={closeHovered ? ds.colorValue.icon.primary : ds.colorValue.icon.secondary} />
                </Pressable>
            </Row>
            <SearchScrollArea resetKey={resetScrollKey ?? query} onEndReached={onEndReached}>
                {children}
            </SearchScrollArea>
        </DesktopModal>
    );
};

const styles = StyleSheet.create({
    input: {
        ...textVariants.labelLarge,
        color: ds.colorValue.text.primary,
        flex: 1,
        height: 28,
        outlineWidth: 0,
    },
    closeButton: {
        borderRadius: 100,
        padding: 4,
    },
    scroll: {
        flex: 1,
        scrollbarColor: `${withOpacity(ds.colorValue.background.neutral.stronger, 0.6)} transparent`,
        scrollbarWidth: 'thin',
    } as ViewStyle,
    scrollContent: {
        paddingBottom: 8,
    },
    sticky: {
        position: 'sticky' as ViewStyle['position'],
        top: 0,
        zIndex: 1,
    },
});
